import * as Yup from "yup";
import { useState } from "react";
import { useFormik, Form, FormikProvider } from "formik";
import { useNavigate } from "react-router-dom";
import { httpsCallable } from "firebase/functions";
import { onAuthStateChanged } from "firebase/auth";
import {
  Stack,
  Alert,
  TextField,
  IconButton,
  InputAdornment,
} from "@mui/material";
import { LoadingButton } from "@mui/lab";
import { useSnackbar } from "notistack";
import Iconify from "../../../components/Iconify";

// Import your Firebase auth instance (modular SDK)
import { auth, functions } from "../../../firebase"; 

export default function RegisterForm() {
  const navigate = useNavigate();
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState("");
  const { enqueueSnackbar } = useSnackbar();

  const addMember = httpsCallable(functions, "addMemberToSquareAndFirestore");

  // Extend your schema to validate the email confirmation field.
  const RegisterSchema = Yup.object().shape({
    firstName: Yup.string().required("First name is required"),
    lastName: Yup.string().required("Last name is required"),
    email: Yup.string()
      .email("Email must be a valid email address")
      .required("Email is required"),
    confirmEmail: Yup.string()
      .oneOf([Yup.ref("email"), null], "Emails must match")
      .required("Confirm email is required"),
    password: Yup.string()
      .min(6, "Password must be at least 6 characters long")
      .required("Password is required"),
    confirmPassword: Yup.string()
      .oneOf([Yup.ref("password"), null], "Passwords must match")
      .required("Confirm password is required"),
    phoneNumber: Yup.string().required("Phone number is required"),
    birthday: Yup.date().required("Birthday is required"),
  });

  const formik = useFormik({
    initialValues: {
      firstName: "",
      lastName: "",
      email: "",
      confirmEmail: "",
      password: "",
      confirmPassword: "",
      phoneNumber: "",
      birthday: "",
    },
    validationSchema: RegisterSchema,
    onSubmit: async (values, actions) => {
      setError("");
      try {
        // Prepare member data. We include the password so the Cloud Function can create the Auth user.
        const { password, confirmPassword, confirmEmail, ...restValues } = values;
        const memberData = {
          ...restValues,
          role: "member",
          email: values.email,
          squareCustomerId: null,
          password, // sent to Cloud Function for new user creation
        };

        // Call the Cloud Function to create the user (and add them to Firestore and Square).
        const result = await addMember({ memberData, isEdit: false });
        if (result.data && result.data.success) {
          enqueueSnackbar(result.data.message, { variant: "success" });
          
          // Wait for the auth state to update (using the modular onAuthStateChanged)
          const unsubscribe = onAuthStateChanged(auth, (currentUser) => {
            if (!currentUser) {
              unsubscribe();
              navigate("/login", { replace: true });
            }
          });
        } else {
          throw new Error("Unknown error occurred.");
        }
      } catch (err) {
        console.error("🚨 Registration error:", err);
        setError(err.message || "Registration failed. Please try again.");
        actions.setSubmitting(false);
      }
    },
  });

  const { errors, touched, handleSubmit, isSubmitting, getFieldProps } = formik;

  return (
    <FormikProvider value={formik}>
      <Form autoComplete="off" onSubmit={handleSubmit}>
        <Stack spacing={3}>
          {error && (
            <Alert severity="error" variant="filled">
              {error}
            </Alert>
          )}

          <TextField
            fullWidth
            label="First Name"
            {...getFieldProps("firstName")}
            error={Boolean(touched.firstName && errors.firstName)}
            helperText={touched.firstName && errors.firstName}
          />

          <TextField
            fullWidth
            label="Last Name"
            {...getFieldProps("lastName")}
            error={Boolean(touched.lastName && errors.lastName)}
            helperText={touched.lastName && errors.lastName}
          />

          <TextField
            fullWidth
            autoComplete="email"
            type="email"
            label="Email address"
            {...getFieldProps("email")}
            error={Boolean(touched.email && errors.email)}
            helperText={touched.email && errors.email}
          />

          <TextField
            fullWidth
            autoComplete="email"
            type="email"
            label="Confirm Email address"
            {...getFieldProps("confirmEmail")}
            error={Boolean(touched.confirmEmail && errors.confirmEmail)}
            helperText={touched.confirmEmail && errors.confirmEmail}
          />

          <TextField
            fullWidth
            autoComplete="new-password"
            type={showPassword ? "text" : "password"}
            label="Password"
            {...getFieldProps("password")}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton edge="end" onClick={() => setShowPassword((prev) => !prev)}>
                    <Iconify icon={showPassword ? "eva:eye-fill" : "eva:eye-off-fill"} />
                  </IconButton>
                </InputAdornment>
              ),
            }}
            error={Boolean(touched.password && errors.password)}
            helperText={touched.password && errors.password}
          />

          <TextField
            fullWidth
            type="password"
            label="Confirm Password"
            {...getFieldProps("confirmPassword")}
            error={Boolean(touched.confirmPassword && errors.confirmPassword)}
            helperText={touched.confirmPassword && errors.confirmPassword}
          />

          <TextField
            fullWidth
            label="Phone Number"
            {...getFieldProps("phoneNumber")}
            error={Boolean(touched.phoneNumber && errors.phoneNumber)}
            helperText={touched.phoneNumber && errors.phoneNumber}
          />

          <TextField
            fullWidth
            type="date"
            label="Birthday"
            InputLabelProps={{ shrink: true }}
            {...getFieldProps("birthday")}
            error={Boolean(touched.birthday && errors.birthday)}
            helperText={touched.birthday && errors.birthday}
          />

          <LoadingButton
            fullWidth
            size="large"
            type="submit"
            variant="contained"
            loading={isSubmitting}
          >
            Register
          </LoadingButton>
        </Stack>
      </Form>
    </FormikProvider>
  );
}
